"use client";

import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import { useSession, signOut } from "next-auth/react";
import {
  Button,
  Container,
  Flex,
  HStack,
  Card,
  Icon,
  Link as ChakraLink,
} from "@chakra-ui/react";
import { ColorModeButton } from "@/components/ui/color-mode";
import { useRouter } from "next/navigation";
import MobileNav from "./MobileNav";
import { useLoading } from "@/app/context/LoadingProvider";
import { useNavigationGuard } from "@/app/context/NavigationGuardProvider";
import AvatarMenu from "./AvatarMenu";
import { BrandLogo } from "./BrandLogo";
import { BrandName } from "./BrandName";
import { LuExternalLink } from "react-icons/lu";
import { FaYoutube } from "react-icons/fa6";
import NotificationBell from "./NotificationBell";



export default function Navigation() {
  const { setPageLoading } = useLoading();
  const { guardedPush } = useNavigationGuard();
  const { data: session } = useSession();
  const isLoggedIn = Boolean(session);
  const [redirect, setRedirect] = useState(false);
  const router = useRouter();
  const pathname = usePathname();
  const isEditorPage = pathname === "/Editor";
  const name = session?.user?.name ?? "";
  const image =
    session?.user?.isMinor !== true && session?.user?.showProfileImage !== false
      ? session?.user?.image ?? undefined
      : undefined;

  useEffect(() => {
    setPageLoading(false);
  }, [pathname, setPageLoading]);

  function handleRedirect() {
    setPageLoading(true);
    setRedirect(true);
    router.replace("/SignIn");
    window.setTimeout(() => setRedirect(false), 1500);
  }

  async function handleSignOut() {
  try {
    setPageLoading(true);

    await signOut({
      redirectTo: "/",
    });
  } catch (error) {
    console.error("[SIGN_OUT_ERROR]", error);
    setPageLoading(false);
  }
}

  return (
    <Card.Root
      as="header"
      display={pathname === "/SignIn" || pathname.startsWith("/SignUp") ? "none" : "flex"}
      position="sticky"
      top={0}
      left={0}
      right={0}
      zIndex={1000}
      py={{ base: 2, md: 2 }}
      mb={isEditorPage ? 1 : 0}
    >
      <Container maxW="7xl" px={{ base: 4, md: 6, lg: 8 }}>
        <Flex
          align="center"
          justify="space-between"
          gap={{ base: 3, md: 6 }}
        >
          <HStack gap={2}>
            <BrandLogo />
            <BrandName />
          </HStack>
          <Flex
            align="center"
            gap={{ base: 2, md: 6 }}
            display={{ base: "none", lg: "flex" }}
          >
            <Button
              size="2xs"
              display="inline-flex"
              alignItems="center"
              gap={1.5}
              px={3}
              py={3}
              rounded="full"
              border="1px solid"
              borderColor="blackAlpha.200"
              bg="blackAlpha.50"
              _dark={{ borderColor: "whiteAlpha.300", bg: "whiteAlpha.100" }}
              fontSize="xs"
              fontWeight="semibold"
              color={pathname.startsWith("/blog") ? "teal.500" : "fg"}
              _hover={{
                textDecoration: "none",
                color: "teal.500",
                bg: "blackAlpha.100",
                _dark: { bg: "whiteAlpha.200" },
              }}
              onClick={() => guardedPush("/blog")}
            >
              Blog
              <Icon as={LuExternalLink} boxSize="3" />
            </Button>
            <ChakraLink
              href="https://www.youtube.com/@BongGo2026"
              target="_blank"
              rel="noopener noreferrer"
              color="red.600"
              cursor="pointer"
              aria-label="Visit BongGo on YouTube"
              _hover={{ color: "red.500" }}
              _focusVisible={{ color: "red.500", outline: "none" }}
            >
              <Icon as={FaYoutube} boxSize={6} display="block" />
            </ChakraLink>
            {isLoggedIn ? <NotificationBell /> : null}
            {isLoggedIn ? (
              <Flex justifyContent="center" alignItems="center" gap={4}>
                <ColorModeButton />
                <AvatarMenu
                  signout={handleSignOut}
                  name={name}
                  imgSrc={image}
                />
              </Flex>
            ) : (
              <HStack>
                <ColorModeButton />
                <Button
                  variant="outline"
                  colorPalette="teal"
                  onClick={handleRedirect}
                  size="2xs"
                  loading={redirect}
                  disabled={redirect}
                  fontSize="xs"
                  py={3}
                  px={2}
                >
                  Sign In
                </Button>
              </HStack>
            )}
          </Flex>

          <HStack display={{ base: "flex", lg: "none" }}>
            {isLoggedIn ? (
              <>
                <MobileNav />
              </>
            ) : (
              <Button
                variant="outline"
                colorPalette="teal"
                onClick={handleRedirect}
                size="2xs"
                loading={redirect}
                disabled={redirect}
                px={3}
                py={2}
              >
                Sign In
              </Button>
            )}
          </HStack>
        </Flex>
      </Container>
    </Card.Root>
  );
}
